import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, TagsOutlined } from '@ant-design/icons';
import { Modal, Tag, Switch, Input, Select, Spin, Tooltip } from 'antd';
import { adminService, Product, PricingItem } from '../../services/admin.service';
import { useNotification } from '../../hooks/useNotification';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import ProductPlansManager from '../../components/admin/ProductPlansManager';
import { resolvePricingRef, planMatchesRef } from '../../utils/pricingRef';

const { TextArea } = Input;

interface ProductFormState {
  id: string;
  name: string;
  type: string;
  category: string;
  typeFamily: string;
  description: string;
  isActive: boolean;
  stock: string;
  sku: string;
  allowedPlans: string[];
}

const emptyForm: ProductFormState = {
  id: '',
  name: '',
  type: '',
  category: '',
  typeFamily: '',
  description: '',
  isActive: false,
  stock: '',
  sku: '',
  allowedPlans: [],
};

const AdminProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [plans, setPlans] = useState<PricingItem[]>([]);
  const [addons, setAddons] = useState<PricingItem[]>([]);
  const [fxRate, setFxRate] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(true);

  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [plansModalProduct, setPlansModalProduct] = useState<Product | null>(null);

  const { success: showSuccess, error: showError } = useNotification();

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const [productsRes, pricingRes] = await Promise.all([
      adminService.getProducts(),
      adminService.getPricing(),
    ]);
    if (!productsRes.success) {
      setError(productsRes.error || 'Error al cargar productos');
      showError(`Error al cargar productos: ${productsRes.error}`);
      setProducts([]);
    } else {
      setProducts(productsRes.products);
    }
    // Si pricing falla, la página sigue sirviendo (solo sin columna de planes)
    setPlans(pricingRes.success ? pricingRes.plans : []);
    setAddons(pricingRes.success ? pricingRes.addons : []);
    setFxRate(pricingRes.config?.usdToMxn ?? null);
    setIsLoading(false);
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort(),
    [products],
  );

  /**
   * Planes que el CLIENTE vería para este producto (misma consulta que hace
   * la pantalla de compra), filtrados por allowedPlans si está definido.
   */
  const plansForProduct = useCallback(
    (product: Product): PricingItem[] => {
      const ref = resolvePricingRef(product);
      const matching = plans.filter((p) => planMatchesRef(p, ref));
      if (product.allowedPlans && product.allowedPlans.length > 0) {
        return matching.filter((p) => product.allowedPlans!.includes(p.planCode || ''));
      }
      return matching;
    },
    [plans],
  );

  const filteredProducts = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return products.filter((p) => {
      if (!showInactive && !p.isActive) return false;
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      if (!query) return true;
      return (
        (p.name || '').toLowerCase().includes(query) ||
        (p.id || '').toLowerCase().includes(query) ||
        (p.sku || '').toLowerCase().includes(query)
      );
    });
  }, [products, searchQuery, categoryFilter, showInactive]);

  const openEdit = (product: Product) => {
    setIsCreating(false);
    setEditingProduct(product);
    setForm({
      id: product.id,
      name: product.name || '',
      type: product.type || '',
      category: product.category || '',
      typeFamily: product.typeFamily || '',
      description: product.description || '',
      isActive: !!product.isActive,
      stock: product.stock !== undefined ? String(product.stock) : '',
      sku: product.sku || '',
      allowedPlans: product.allowedPlans || [],
    });
  };

  const openCreate = () => {
    setIsCreating(true);
    setEditingProduct(null);
    setForm(emptyForm);
  };

  const closeModal = () => {
    setEditingProduct(null);
    setIsCreating(false);
    setForm(emptyForm);
  };

  /** Opciones de plan para el multi-select: planes que resolvería el producto del form. */
  const planOptions = useMemo(() => {
    const syntheticProduct = {
      category: form.category,
      typeFamily: form.typeFamily,
      osType: editingProduct?.osType,
      osFamily: editingProduct?.osFamily,
      engine: editingProduct?.engine,
    } as Product;
    const ref = resolvePricingRef(syntheticProduct);
    const familyPlans = plans.filter((p) => planMatchesRef(p, ref));
    const seen = new Set<string>();
    return familyPlans
      .filter((p) => {
        const code = p.planCode || '';
        if (!code || seen.has(code)) return false;
        seen.add(code);
        return true;
      })
      .map((p) => ({
        value: p.planCode!,
        label: `${p.planCode} — ${p.planName || p.displayName || ''}`,
      }));
  }, [plans, form.category, form.typeFamily, editingProduct]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload: Partial<Product> = {
        name: form.name.trim(),
        type: form.type.trim(),
        category: form.category.trim(),
        typeFamily: form.typeFamily.trim(),
        description: form.description,
        isActive: form.isActive,
        stock: form.stock ? Number(form.stock) : undefined,
        sku: form.sku || undefined,
        allowedPlans: form.allowedPlans,
      };
      if (isCreating) {
        const response = await adminService.createProduct({ ...payload, id: form.id.trim() });
        if (!response.success) {
          showError(`Error al crear producto: ${response.error}`);
          return;
        }
        showSuccess(`Producto ${form.id} creado`);
      } else if (editingProduct) {
        const response = await adminService.updateProduct(editingProduct.id, payload);
        if (!response.success) {
          showError(`Error al actualizar producto: ${response.error}`);
          return;
        }
        showSuccess(`Producto ${editingProduct.id} actualizado`);
      }
      closeModal();
      loadData();
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (product: Product, isActive: boolean) => {
    const response = await adminService.updateProduct(product.id, { isActive });
    if (!response.success) {
      showError(`Error al cambiar estado: ${response.error}`);
      return;
    }
    showSuccess(`${product.name} ${isActive ? 'activado' : 'desactivado'}`);
    setProducts((prev) =>
      prev.map((p) => (p.id === product.id ? { ...p, isActive } : p)),
    );
  };

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const fieldWrapClass = 'mb-4';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Productos</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              El producto define el <em>qué</em>; el precio lo definen sus planes (sección Precios)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <PlusOutlined /> Nuevo producto
            </button>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ReloadOutlined /> Actualizar
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[220px]">
            <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre, id o SKU…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">Todas las categorías</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <Switch size="small" checked={showInactive} onChange={setShowInactive} />
            Mostrar inactivos
          </label>
          <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
            {filteredProducts.length} de {products.length} productos
          </span>
        </div>

        {/* Contenido */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Spin size="large" />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            title="Sin productos"
            description="No hay productos que coincidan con los filtros."
          />
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['Producto', 'Categoría', 'Familia', 'Planes', 'Estado', 'Actualizado', ''].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredProducts.map((product) => {
                    const productPlans = plansForProduct(product);
                    return (
                      <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{product.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{product.id}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{product.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{product.typeFamily}</td>
                        <td className="px-4 py-3">
                          {productPlans.length > 0 ? (
                            <Tooltip title="Administrar planes, variantes y precios de este producto">
                              <button
                                onClick={() => setPlansModalProduct(product)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              >
                                <TagsOutlined /> {productPlans.length} {productPlans.length === 1 ? 'plan' : 'planes'}
                              </button>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Sin planes en el catálogo — abre para crear el primero">
                              <button
                                onClick={() => setPlansModalProduct(product)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-sm text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                              >
                                <TagsOutlined /> sin planes — configurar
                              </button>
                            </Tooltip>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              size="small"
                              checked={!!product.isActive}
                              onChange={(checked) => handleToggleActive(product, checked)}
                            />
                            <Tag color={product.isActive ? 'green' : 'default'}>
                              {product.isActive ? 'ACTIVO' : 'INACTIVO'}
                            </Tag>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {product.updatedAt ? new Date(product.updatedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openEdit(product)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          >
                            <EditOutlined /> Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Gestión de planes/variantes del producto */}
        <ProductPlansManager
          product={plansModalProduct}
          plans={plans}
          addons={addons}
          fxRate={fxRate}
          onClose={() => setPlansModalProduct(null)}
          onChanged={loadData}
        />

        {/* Modal de edición / creación */}
        <Modal
          title={isCreating ? 'Nuevo producto' : `Editar producto — ${editingProduct?.name ?? ''}`}
          open={isCreating || editingProduct !== null}
          onCancel={closeModal}
          onOk={handleSave}
          okText={isCreating ? 'Crear' : 'Guardar cambios'}
          cancelText="Cancelar"
          confirmLoading={isSaving}
          destroyOnHidden
        >
          <div className="pt-4">
            {isCreating && (
              <div className={fieldWrapClass}>
                <label className={labelClass}>ID (único, no editable después)</label>
                <Input
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                  placeholder="ej. ec2-ubuntu-24-x86"
                />
              </div>
            )}
            <div className={fieldWrapClass}>
              <label className={labelClass}>Nombre</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className={fieldWrapClass}>
                <label className={labelClass}>Tipo</label>
                <Input
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  placeholder="COMPUTE"
                />
              </div>
              <div className={fieldWrapClass}>
                <label className={labelClass}>Categoría</label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="VM"
                />
              </div>
              <div className={fieldWrapClass}>
                <label className={labelClass}>Familia</label>
                <Input
                  value={form.typeFamily}
                  onChange={(e) => setForm({ ...form, typeFamily: e.target.value, allowedPlans: [] })}
                  placeholder="EC2"
                />
              </div>
            </div>
            <div className={fieldWrapClass}>
              <label className={labelClass}>Descripción</label>
              <TextArea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className={fieldWrapClass}>
              <label className={labelClass}>
                Planes permitidos (vacío = todos los de la familia {form.typeFamily || '…'})
              </label>
              <Select
                mode="multiple"
                className="w-full"
                value={form.allowedPlans}
                onChange={(value) => setForm({ ...form, allowedPlans: value })}
                options={planOptions}
                placeholder={planOptions.length ? 'Todos los planes de la familia' : 'La familia no tiene planes en el catálogo'}
                disabled={planOptions.length === 0}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className={fieldWrapClass}>
                <label className={labelClass}>Stock</label>
                <Input
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  placeholder="999"
                />
              </div>
              <div className={fieldWrapClass}>
                <label className={labelClass}>SKU</label>
                <Input
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Switch
                checked={form.isActive}
                onChange={(checked) => setForm({ ...form, isActive: checked })}
              />
              Visible para clientes (isActive)
            </label>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AdminProductsPage;
